import { DomainLocale, Locale } from '~/App/shared/localization/types';
import { environment } from '~/config/public/environment';
import {
  BASE_DEV_URLS,
  BASE_URLS,
  DOMAIN_PATH_LOCALES,
  LOCALE_PATH_SEGMENTS
} from '~/config/constants';
import { Environment } from '~/config/types';
import type { SiteLocale } from '~/config/generated/graphql';

/**
 * Maps an internal BCP-47 locale code to its datoCMS `SiteLocale` identifier.
 * datoCMS uses an underscore for the region subtag (e.g. `en-SE` → `en_SE`);
 * locales without a region are unchanged.
 */
export const toCmsLocale = (locale: string): SiteLocale =>
  locale.replace('-', '_') as SiteLocale;

/**
 * Maps a datoCMS `SiteLocale` identifier back to our internal BCP-47 locale
 * code (e.g. `en_SE` → `en-SE`). Inverse of `toCmsLocale`.
 */
export const fromCmsLocale = (locale: string): Locale =>
  locale.replace('_', '-') as Locale;

/**
 * The URL path segment for a locale. Equals the locale code except where
 * `LOCALE_PATH_SEGMENTS` overrides it (e.g. `en-SE` → `en`).
 */
export const localeToPathSegment = (locale: Locale): string =>
  LOCALE_PATH_SEGMENTS[locale] ?? locale;

/**
 * Resolve a URL path segment to a locale, scoped to the current domain. The
 * same segment can mean different locales per domain — `/en/` is `en-SE` on
 * kvd.se but the bare `en` on kvdcars.com — so the domain is required.
 * Returns `undefined` when the segment is not a locale path on this domain.
 */
export const pathSegmentToLocale = (
  segment: string,
  domainLocale: DomainLocale
): Locale | undefined =>
  // `?? []` guards against a misconfigured `process.env.LOCALE` (anything other
  // than sv/en) so the middleware degrades instead of throwing on every request.
  (DOMAIN_PATH_LOCALES[domainLocale] ?? []).find(
    locale => localeToPathSegment(locale) === segment
  );

/**
 * Which domain a locale lives on. `sv` and `en-SE` are served on kvd.se; every
 * other locale (the kvdcars.com export set, incl. bare `en`) lives on
 * kvdcars.com. Distinct from `getSvOrEnDomainLocale`, which buckets locales for
 * date/asset formatting rather than for domain routing.
 *
 * Derived from `DOMAIN_PATH_LOCALES` so the domain mapping has a single source
 * of truth; falls back to the export domain (`en`) for an unlisted locale.
 */
export const getDomainForLocale = (locale: Locale): DomainLocale =>
  (Object.keys(DOMAIN_PATH_LOCALES) as DomainLocale[]).find(domain =>
    DOMAIN_PATH_LOCALES[domain].includes(locale)
  ) ?? 'en';

export const trimLocalePathSegment = (path: string, locale: Locale) => {
  // Strip by the URL *segment*, which differs from the locale code for en-SE
  // (segment `en`). Using the raw code would leave the `/en` prefix in place
  // and break route matching.
  const segment = localeToPathSegment(locale);
  const splitPath = path.split('/');
  if (splitPath?.[1] !== segment) {
    return path;
  }
  return splitPath.filter(elem => elem !== segment).join('/');
};

export const toBackendLocale = (locale: Locale): Locale =>
  locale === 'en-SE' ? 'en' : locale;

export const toBackendDomainLocale = (locale: Locale | null): DomainLocale =>
  locale && locale !== 'sv' ? 'en' : 'sv';

export const isExportDomain = (domainLocale: DomainLocale | null) =>
  domainLocale === 'en';

export const isSwedishDomainLocale = (locale: unknown) =>
  DOMAIN_PATH_LOCALES.sv.some(
    swedishDomainLocale => swedishDomainLocale === locale
  );

export const getSvOrEnDomainLocale = (locale: Locale | null) => {
  return locale === 'en' ? 'en' : 'sv';
};

export const getRedirectBaseUrl = ({
  requestedlocale,
  domainLocale,
  originUrl
}: {
  requestedlocale: Locale;
  domainLocale: DomainLocale | null;
  originUrl: string;
}) => {
  if (!domainLocale) {
    return originUrl;
  }

  const redirectLocale: DomainLocale = getDomainForLocale(requestedlocale);

  if (environment === Environment.Production) {
    return BASE_URLS[redirectLocale];
  }
  if (environment === Environment.Local) {
    return BASE_DEV_URLS[redirectLocale];
  }
  if (redirectLocale === domainLocale) {
    return originUrl;
  }

  const pattern = domainLocale !== 'sv' ? 'kvdcars-com' : 'kvd-se';
  const replacePhrase = domainLocale === 'sv' ? 'kvdcars-com' : 'kvd-se';
  return originUrl.replace(pattern, replacePhrase);
};

export const trimTrailingSlash = (path: string) => path.replace(/\/$/, '');

export const trimLeadingSlash = (path: string) => path.replace(/^\/+/, '');

export const addLeadingSlash = (path: string) => `/${trimLeadingSlash(path)}`;
